import { useMemo, useState } from "react";

import {
  claimPlayer,
  friendlyError,
  mergeMemberIdentity,
  newActionId,
  timed,
} from "../../data/callables";
import { useTeamCollection } from "../../hooks/useMembership";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../shared/ui/alert-dialog";
import { Badge } from "../../shared/ui/badge";
import { Button } from "../../shared/ui/button";
import { FieldError } from "../../shared/ui/field";
import { Spinner } from "../../shared/ui/spinner";
import {
  OnboardingContent,
  OnboardingFrame,
  OnboardingHeading,
} from "./OnboardingFrame";
import type { AuthAction } from "./onboardingTypes";
import { errorCode } from "./onboardingTypes";

interface PendingClaim {
  playerId: string;
  replaceExistingClaim: boolean;
  expectedPreviousUid: string | null;
}

interface ClaimGateProps {
  teamId: string;
  linkGoogle?: AuthAction | null;
}

export function ClaimGate({ teamId, linkGoogle }: ClaimGateProps) {
  const { docs: players } = useTeamCollection(teamId, "players");
  const { docs: claims } = useTeamCollection(teamId, "claims");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [pendingClaim, setPendingClaim] = useState<PendingClaim | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [linkState, setLinkState] = useState<string | null>(null);

  const claimsByPlayerId = useMemo(
    () => new Map((claims ?? []).map((claim) => [claim.id, claim.uid])),
    [claims],
  );
  const roster = useMemo(
    () =>
      (players ?? [])
        .filter((player) => player.active !== false)
        .map((player) => ({
          ...player,
          claimedUid: claimsByPlayerId.get(player.id) ?? null,
          claimed: claimsByPlayerId.has(player.id),
        }))
        .sort((left, right) => left.name.localeCompare(right.name)),
    [players, claimsByPlayerId],
  );
  const anyAvailable = roster.some((player) => !player.claimed);

  const claim = async ({
    playerId,
    replaceExistingClaim,
    expectedPreviousUid,
  }: PendingClaim) => {
    setBusyId(playerId);
    setError(null);
    try {
      await timed(
        "claimPlayer",
        claimPlayer({
          teamId,
          actionId: newActionId(),
          playerId,
          replaceExistingClaim,
          ...(replaceExistingClaim && expectedPreviousUid
            ? { expectedPreviousUid }
            : {}),
        }),
      );
    } catch (claimError) {
      setError(friendlyError(claimError));
      setBusyId(null);
      setPendingClaim(null);
    }
  };

  const pendingPlayer =
    roster.find((player) => player.id === pendingClaim?.playerId) ?? null;
  const replacingClaim = pendingClaim?.replaceExistingClaim === true;

  const link = async () => {
    if (!linkGoogle) return;
    setLinkState("busy");
    try {
      await linkGoogle();
      await mergeMemberIdentity({ teamId });
      setLinkState("done");
    } catch (linkError) {
      const code = errorCode(linkError);
      if (
        code === "auth/popup-closed-by-user" ||
        code === "auth/cancelled-popup-request"
      ) {
        setLinkState(null);
      } else if (code === "auth/credential-already-in-use") {
        setLinkState("That Google account is already linked to someone else.");
      } else {
        setLinkState(friendlyError(linkError));
      }
    }
  };

  return (
    <OnboardingFrame eyebrow="Choose your roster identity">
      <OnboardingHeading
        title="WHO ARE YOU?"
        description="Claim your roster spot. If your name is already claimed, select it to recover it from an old session."
      />
      <OnboardingContent>
        {players === undefined || claims === undefined ? (
          <div className="flex items-center justify-center gap-2 py-4 text-sm text-foreground-muted">
            <Spinner />
            Loading roster…
          </div>
        ) : (
          <>
            {!anyAvailable ? (
              <p className="m-0 rounded-[var(--radius-sm)] border border-warning/60 bg-warning/10 p-3 text-sm text-warning">
                Every roster player is already claimed. Select your name to
                recover it, or ask a teammate to add you to the roster.
              </p>
            ) : null}
            <div className="grid max-h-[48svh] gap-1.5 overflow-y-auto overscroll-contain pr-0.5">
              {roster.map((player) => (
                <Button
                  key={player.id}
                  type="button"
                  onClick={() =>
                    setPendingClaim({
                      playerId: player.id,
                      replaceExistingClaim: player.claimed,
                      expectedPreviousUid: player.claimedUid,
                    })
                  }
                  disabled={busyId !== null}
                  variant="outline"
                  className="w-full justify-between px-3"
                >
                  <span className="truncate">{player.name}</span>
                  <span className="flex shrink-0 items-center gap-2">
                    {player.number != null ? (
                      <span className="font-mono text-xs text-warning">
                        #{player.number}
                      </span>
                    ) : null}
                    {player.claimed ? (
                      <Badge variant="neutral">CLAIMED</Badge>
                    ) : null}
                  </span>
                </Button>
              ))}
            </div>
          </>
        )}
        {error ? (
          <FieldError className="text-center">{error}</FieldError>
        ) : null}
        {linkGoogle ? (
          <div className="mt-1 border-t border-border pt-3 text-center">
            {linkState === "done" ? (
              <p className="m-0 text-sm font-semibold text-success">
                Google account linked ✓
              </p>
            ) : (
              <Button
                type="button"
                onClick={link}
                pending={linkState === "busy"}
                pendingLabel="LINKING…"
                size="sm"
                variant="ghost"
              >
                Link Google account (optional)
              </Button>
            )}
            {linkState && linkState !== "busy" && linkState !== "done" ? (
              <FieldError className="mt-2">{linkState}</FieldError>
            ) : null}
          </div>
        ) : null}
      </OnboardingContent>

      <AlertDialog
        open={pendingPlayer !== null}
        onOpenChange={(open) => {
          if (!open && busyId === null) setPendingClaim(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {replacingClaim ? "Reclaim" : "Claim"} {pendingPlayer?.name} as
              your player?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {replacingClaim
                ? "This roster spot is linked to another session. Only continue if this is you; the old session will lose its claim."
                : "This connects your membership to this roster spot."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busyId !== null}>
              CANCEL
            </AlertDialogCancel>
            <Button
              type="button"
              onClick={() => pendingClaim && claim(pendingClaim)}
              pending={busyId === pendingPlayer?.id}
              pendingLabel={replacingClaim ? "RECLAIMING…" : "CLAIMING…"}
            >
              {replacingClaim
                ? `YES, RECLAIM ${pendingPlayer?.name.toUpperCase()}`
                : `YES, I'M ${pendingPlayer?.name.toUpperCase()}`}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </OnboardingFrame>
  );
}
